import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, X } from 'lucide-react';
import { ClienteConsolidado } from '../types';
import { formatCurrency, formatKwh, formatPercent } from '../utils';
import PerfilCliente360 from './PerfilCliente360';

interface Props {
  clientes: ClienteConsolidado[];
}

const RankingDevedores: React.FC<Props> = ({ clientes }) => {
  const top = clientes.filter(c => !c.interno).slice(0, 50);
  const [clienteParaPerfil, setClienteParaPerfil] = useState<string | null>(null);

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Ranking dos Maiores Devedores (Top 50)</h2>
        <p className="text-sm text-gray-500">Foco estratégico para recuperação financeira</p>
      </div>

      <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-[#1d220f] text-white sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 font-bold">#</th>
                <th className="px-4 py-3 font-bold">Cliente</th>
                <th className="px-4 py-3 font-bold text-right">Valor em Aberto</th>
                <th className="px-4 py-3 font-bold text-center">Faturas</th>
                <th className="px-4 py-3 font-bold text-right">Saldo Retido</th>
                <th className="px-4 py-3 font-bold text-center">Risco</th>
                <th className="px-4 py-3 font-bold text-right">% Carteira</th>
                <th className="px-4 py-3 font-bold text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {top.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Nenhum devedor encontrado.</td></tr>
              ) : (
                top.map((c, i) => (
                  <tr key={c.nome} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-black text-gray-400">{i + 1}</td>
                    <td className="px-4 py-3 font-bold text-gray-800">{c.nome}</td>
                    <td className="px-4 py-3 text-right font-black text-red-600">{formatCurrency(c.valorTotal)}</td>
                    <td className="px-4 py-3 text-center text-gray-600 font-semibold">{c.qtdFaturas}</td>
                    <td className="px-4 py-3 text-right text-blue-600 font-semibold">{formatKwh(c.saldoAcumulado)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                        c.risco === 'RISCO ESTRATÉGICO ALTO' ? 'bg-red-100 text-red-700' :
                        c.risco === 'RISCO ALTO' ? 'bg-orange-100 text-orange-700' :
                        c.risco === 'RISCO MÉDIO' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {c.risco}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 text-xs font-semibold">{formatPercent(c.pct)}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        title="Detalhes do Cliente (Perfil 360)"
                        onClick={() => setClienteParaPerfil(c.nome)}
                        className="text-[#F6A937] hover:text-orange-500 transition-colors p-1 rounded hover:bg-orange-50"
                      >
                        <ExternalLink size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {clienteParaPerfil && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-6xl h-[90vh] bg-gray-50 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 bg-[#1d220f] border-b border-[#2a3017]">
              <h3 className="text-white font-black text-sm tracking-tight">Perfil 360º do Cliente</h3>
              <button
                onClick={() => setClienteParaPerfil(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <PerfilCliente360 clientes={clientes} defaultClientName={clienteParaPerfil} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RankingDevedores;
